import "../stores/query";
import { INITIAL_ROUNDS } from "../stores/constants";
import bs58 from "bs58";
import { CONFIG } from "../config";
import { GraphQLClient } from "graphql-request";
const oneEther = BigInt(1000000000000000000);
export function nFormatter(num, digits) {
    if (num === 0) {
        return num;
    }
    else if (num < 0) {
        num *= -1;
    }
    if (num < 1) {
        return num.toFixed(digits);
    }
    const lookup = [
        { value: 1, symbol: "" },
        { value: 1e3, symbol: "k" },
        { value: 1e6, symbol: "M" },
        { value: 1e9, symbol: "G" },
        { value: 1e12, symbol: "T" },
        { value: 1e15, symbol: "P" },
        { value: 1e18, symbol: "E" }
    ];
    const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
    var item = lookup.slice().reverse().find(function (item) {
        return num >= item.value;
    });
    return item ? (num / item.value).toFixed(digits).replace(rx, "$1") + item.symbol : "0";
}
export function convertIpfsImage(url) {
    if (url.startsWith('ipfs://')) {
        return `https://cloudflare-ipfs.com/ipfs/${url.slice(7)}`;
    }
    return url;
}
export async function getNftImage(chainId, address, tokenId) {
    const config = CONFIG[chainId];
    if (config.ALCHEMY_URL === '') {
        return '/placeholder.svg';
    }
    try {
        const res = await fetch(`${config.ALCHEMY_URL}/getNFTMetadata?contractAddress=${address}&tokenId=${tokenId}&tokenType=erc721`);
        const image = (await res.json()).metadata.image;
        if (image === undefined || image === null) {
            return '/placeholder.svg';
        }
        return convertIpfsImage(image);
    }
    catch (e) {
        return '/placeholder.svg';
    }
}
export async function getNftCollection(nftContract) {
    try {
        return await nftContract.methods.name().call();
    }
    catch (e) {
        console.error(e);
        return 'Unknown';
    }
}
export function yearPairRewardPerRound(pairHourData) {
    const now = Date.now() / 1000;
    let dayPairReward = 0;
    let dayOpenTs;
    for (let hourData of pairHourData) {
        dayPairReward += Number.parseFloat(hourData.pairReward);
        const ts = Number.parseInt(hourData.periodStart);
        dayOpenTs = ts;
        if (now - ts > 86400) {
            break;
        }
    }
    const pairRewardPerRound = dayPairReward / INITIAL_ROUNDS;
    return pairRewardPerRound / (now - dayOpenTs) * 60 * 60 * 24 * 365;
}
export async function convertPairData(chainId, data) {
    let pair = {};
    pair.id = Number.parseInt(data.id);
    pair.nftAddress = data.nftAddress;
    pair.tokenId = Number.parseInt(data.tokenId);
    pair.title = data.title;
    // @ts-ignore
    pair.denominator = { symbol: data.baseToken.symbol, decimals: data.baseToken.decimals };
    pair.roundTokenPrice = Number.parseFloat(data.roundTokenPrice);
    pair.category = data.category;
    pair.collection = data.collection;
    pair.nftPrice = Number.parseFloat(data.nftPrice);
    if (data.pairHourData.length === 25) {
        const roundPriceLastDay = Number.parseFloat(data.pairHourData[data.pairHourData.length - 1].close);
        pair.roundPriceChange = (pair.roundTokenPrice - roundPriceLastDay) / roundPriceLastDay * 100;
        const roundSupplyLastDay = data.pairHourData[data.pairHourData.length - 1].roundTokenTotalSupply;
        const nftPriceLastDay = Number.parseInt(roundSupplyLastDay) * roundPriceLastDay / 1e18;
        pair.nftPriceChange = (pair.nftPrice - nftPriceLastDay) / nftPriceLastDay * 100;
    }
    else {
        const roundPriceInitial = Number.parseFloat(data.pairHourData[0].open);
        pair.roundPriceChange = (pair.roundTokenPrice - roundPriceInitial) / roundPriceInitial * 100;
        const nftPriceInitial = INITIAL_ROUNDS * roundPriceInitial;
        pair.nftPriceChange = (pair.nftPrice - nftPriceInitial) / nftPriceInitial * 100;
    }
    pair.imageUrl = await getNftImage(chainId, pair.nftAddress, pair.tokenId);
    pair.yearPairRewardPerRound = yearPairRewardPerRound(data.pairHourData);
    return pair;
}
export function shortenAddress(address) {
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
}
export async function convertRound(chainId, row) {
    const resultRow = {};
    const pair = row.pair;
    resultRow['image'] = await getNftImage(chainId, pair.nftAddress, Number.parseInt(pair.tokenId));
    resultRow['title'] = pair.title;
    resultRow['baseToken'] = pair.baseToken.symbol;
    resultRow['baseTokenDecimals'] = Number.parseInt(pair.baseToken.decimals);
    resultRow['metaNftId'] = pair.id;
    resultRow['activated'] = pair.activated;
    const roundTokenBalance = Number.parseInt(row.roundTokenBalance) / 1e18;
    const roundValue = roundTokenBalance * Number.parseFloat(row.pair.roundTokenPrice);
    resultRow['roundValue'] = roundValue;
    resultRow['averageCost'] = Number.parseFloat(row.averageCost);
    const roundCost = roundTokenBalance * resultRow['averageCost'];
    resultRow['roundValueDelta'] = roundValue - roundCost;
    resultRow['roundValueDeltaRate'] = resultRow['roundValueDelta'] / roundCost * 100;
    resultRow['yearPairRewardPerRound'] = yearPairRewardPerRound(pair.pairHourData);
    return resultRow;
}
export async function convertRounds(chainId, data) {
    let result = [];
    for (let row of data) {
        result.push(convertRound(chainId, row));
    }
    return result;
}
export async function convertMetaNft(chainId, row) {
    const resultRow = {};
    resultRow['image'] = await getNftImage(chainId, row.nftAddress, Number.parseInt(row.tokenId));
    resultRow['title'] = row.title;
    resultRow['baseToken'] = row.baseToken.symbol;
    resultRow['baseTokenDecimals'] = Number.parseInt(row.baseToken.decimals);
    resultRow['metaNftId'] = row.id;
    resultRow['nftPrice'] = row.nftPrice;
    resultRow['pairReward'] = Number.parseFloat(row.earnedReward) - Number.parseFloat(row.claimedReward);
    resultRow['yearPairRewardPerRound'] = yearPairRewardPerRound(row.pairHourData);
    resultRow['listingPrice'] = Number.parseFloat(row.pairDayData[0].open);
    const listingPrice = INITIAL_ROUNDS * Number.parseFloat(row.pairDayData[0].open);
    resultRow['nftPriceDelta'] = resultRow['nftPrice'] - listingPrice;
    return resultRow;
}
export async function convertMetaNfts(chainId, data) {
    let result = [];
    for (let row of data) {
        result.push(convertMetaNft(chainId, row));
    }
    return result;
}
export async function convertBid(chainId, row) {
    const resultRow = {};
    const pair = row.pair;
    resultRow['metaNftId'] = row.id;
    resultRow['image'] = await getNftImage(chainId, pair.nftAddress, Number.parseInt(pair.tokenId));
    resultRow['title'] = pair.title;
    resultRow['baseToken'] = pair.baseToken.symbol;
    resultRow['baseTokenDecimals'] = Number.parseInt(pair.baseToken.decimals);
    resultRow['amountIn'] = Number.parseFloat(row.amountIn);
    const expiry = new Date(Number.parseInt(row.expiry) * 1000);
    resultRow['expiry'] = expiry.toUTCString();
    resultRow['metaNftId'] = Number.parseInt(pair.id);
    resultRow['status'] = row.status;
    resultRow['isNft'] = row.isNft;
    if (row.status === 'ACTIVE') {
        if (Date.now() > Number.parseInt(row.expiry) * 1000) {
            resultRow['status'] = 'EXPIRED';
        }
    }
    resultRow['action'] = resultRow['status'] === 'ACTIVE' ? '' : 'Withdraw';
    return resultRow;
}
export async function convertBids(chainId, data) {
    let result = [];
    for (let row of data) {
        result.push(convertBid(chainId, row));
    }
    return result;
}
const fromHexString = hexString => new Uint8Array(hexString.match(/.{1,2}/g).map(byte => parseInt(byte, 16)));
const toHexString = bytes => bytes.reduce((str, byte) => str + byte.toString(16).padStart(2, '0'), '');
export const encodeDescriptionHash = (hash) => {
    return bs58.encode(fromHexString('1220' + hash.slice(2)));
};
export const decodeDescriptionHash = (hash) => {
    return `0x${toHexString(bs58.decode(hash)).slice(4)}`;
};
export function timeConverter(millis) {
    const a = new Date(millis);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const year = a.getFullYear();
    const month = months[a.getMonth()];
    const date = a.getDate();
    const hour = a.getHours();
    const min = a.getMinutes();
    return `${month} ${date}, ${year} ${hour}:${min}`;
}
export function txLink(hash, chainId) {
    if (chainId === 1 || chainId === '0x1') {
        return `https://etherscan.io/tx/${hash}`;
    }
    else if (chainId === 3 || chainId === '0x3') {
        return `https://ropsten.etherscan.io/tx/${hash}`;
    }
    else if (chainId === 4 || chainId === '0x4') {
        return `https://rinkeby.etherscan.io/tx/${hash}`;
    }
    else if (chainId === 137 || chainId === '0x89') {
        return `https://polygonscan.com/tx/${hash}`;
    }
    else if (chainId === 10 || chainId === '0xa') {
        return `https://optimistic.etherscan.io/tx/${hash}`;
    }
    else if (chainId === 42161 || chainId === '0xa4b1') {
        return `https://arbiscan.io/tx/${hash}`;
    }
    else {
        return `/`;
    }
}
export function accountLink(address, chainId) {
    if (chainId === 1 || chainId === '0x1') {
        return `https://etherscan.io/address/${address}`;
    }
    else if (chainId === 3 || chainId === '0x3') {
        return `https://ropsten.etherscan.io/address/${address}`;
    }
    else if (chainId === 4 || chainId === '0x4') {
        return `https://rinkeby.etherscan.io/address/${address}`;
    }
    else if (chainId === 137 || chainId === '0x89') {
        return `https://polygonscan.com/address/${address}`;
    }
    else if (chainId === 10 || chainId === '0xa') {
        return `https://optimistic.etherscan.io/address/${address}`;
    }
    else if (chainId === 42161 || chainId === '0xa4b1') {
        return `https://arbiscan.io/address/${address}`;
    }
    else {
        return `/`;
    }
}
export function nftDetailLink(address, tokenId, chainId) {
    if (chainId === 1 || chainId === '0x1') {
        return `https://opensea.io/assets/${address}/${tokenId}`;
    }
    else if (chainId === 3 || chainId === '0x3') {
        return `https://ropsten.etherscan.io/token/${address}?a=${tokenId}`;
    }
    else if (chainId === 4 || chainId === '0x4') {
        return `https://testnets.opensea.io/assets/${address}/${tokenId}`;
    }
    else if (chainId === 137 || chainId === '0x89') {
        return `https://opensea.io/assets/matic/${address}/${tokenId}`;
    }
    else if (chainId === 10 || chainId === '0xa') {
        return `https://optimistic.etherscan.io/token/${address}?a=${tokenId}`;
    }
    else if (chainId === 42161 || chainId === '0xa4b1') {
        return `https://arbiscan.io/token/${address}?a=${tokenId}`;
    }
    else {
        return `/`;
    }
}
export function nftCollectionLink(address, chainId) {
    if (chainId === 1 || chainId === '0x1') {
        return `https://opensea.io/assets/${address}`;
    }
    else if (chainId === 3 || chainId === '0x3') {
        return `https://ropsten.etherscan.io/token/${address}`;
    }
    else if (chainId === 4 || chainId === '0x4') {
        return `https://testnets.opensea.io/assets/${address}`;
    }
    else if (chainId === 137 || chainId === '0x89') {
        return `https://opensea.io/assets/matic/${address}`;
    }
    else if (chainId === 10 || chainId === '0xa') {
        return `https://optimistic.etherscan.io/token/${address}`;
    }
    else if (chainId === 42161 || chainId === '0xa4b1') {
        return `https://arbiscan.io/token/${address}`;
    }
    else {
        return `/`;
    }
}
const _graphQLClients = {};
Object.entries(CONFIG).forEach(([id, config]) => {
    _graphQLClients[id] = new GraphQLClient(config.GRAPH_URL);
});
export const graphQLClients = _graphQLClients;
